export interface AllowanceRate {
  country: string;
  period: { from: string; to: string };
  ratePerKm: number;
  source: string;
}

// ─── Barèmes officiels — facile à mettre à jour ───────────────────────────────
export const RATES: AllowanceRate[] = [
  // Belgique — trimestriel
  { country: 'BE', period: { from: '2026-04-01', to: '2026-06-30' }, ratePerKm: 0.4327, source: 'SPF Finances' },
  { country: 'BE', period: { from: '2026-01-01', to: '2026-03-31' }, ratePerKm: 0.4326, source: 'SPF Finances' },
  { country: 'BE', period: { from: '2025-10-01', to: '2025-12-31' }, ratePerKm: 0.4293, source: 'SPF Finances' },
  { country: 'BE', period: { from: '2025-07-01', to: '2025-09-30' }, ratePerKm: 0.4280, source: 'SPF Finances' },
  { country: 'BE', period: { from: '2025-04-01', to: '2025-06-30' }, ratePerKm: 0.4265, source: 'SPF Finances' },
  { country: 'BE', period: { from: '2025-01-01', to: '2025-03-31' }, ratePerKm: 0.4254, source: 'SPF Finances' },
  // Belgique — annuel (alternatif)
  { country: 'BE_ANNUAL', period: { from: '2025-07-01', to: '2026-06-30' }, ratePerKm: 0.4449, source: 'Moniteur Belge' },
  // Luxembourg — fixe
  { country: 'LU', period: { from: '2015-06-19', to: '2099-12-31' }, ratePerKm: 0.30, source: 'Règlement GC 19/06/2015' },
  // France — par puissance fiscale (simplifié, taux moyen)
  { country: 'FR', period: { from: '2025-01-01', to: '2025-12-31' }, ratePerKm: 0.357, source: 'Barème fiscal 5CV' },
  // Allemagne
  { country: 'DE', period: { from: '2025-01-01', to: '2025-12-31' }, ratePerKm: 0.30, source: 'Pendlerpauschale' },
  // Pays-Bas
  { country: 'NL', period: { from: '2025-01-01', to: '2025-12-31' }, ratePerKm: 0.23, source: 'Belastingdienst' },
];

// ─── Pays supportés ───────────────────────────────────────────────────────────
export type SupportedCountry = 'BE' | 'BE_ANNUAL' | 'LU' | 'FR' | 'DE' | 'NL';

export const COUNTRY_LABELS: Record<SupportedCountry, string> = {
  BE: 'Belgique (trimestriel)',
  BE_ANNUAL: 'Belgique (annuel)',
  LU: 'Luxembourg',
  FR: 'France',
  DE: 'Allemagne',
  NL: 'Pays-Bas',
};

// ─── Helpers internes ─────────────────────────────────────────────────────────
const toDateStr = (d: Date): string => d.toISOString().slice(0, 10);

/**
 * Retourne le taux applicable pour un pays et une date donnée.
 * Retourne 0 si aucun barème ne correspond.
 */
export function getRate(country: string, date: Date): number {
  const dateStr = toDateStr(date);
  const match = RATES.find(
    r =>
      r.country === country &&
      dateStr >= r.period.from &&
      dateStr <= r.period.to
  );
  return match?.ratePerKm ?? 0;
}

/**
 * Calcule l'indemnité pour une distance, un pays et une date.
 */
export function calculateAllowance(
  distanceKm: number,
  country: string,
  date: Date
): { rate: number; amount: number; source: string } {
  const dateStr = toDateStr(date);
  const match = RATES.find(
    r =>
      r.country === country &&
      dateStr >= r.period.from &&
      dateStr <= r.period.to
  );
  const rate = match?.ratePerKm ?? 0;
  return {
    rate,
    amount: parseFloat((distanceKm * rate).toFixed(2)),
    source: match?.source ?? 'Inconnu',
  };
}

/**
 * Calcule les totaux mensuels pour une liste de trajets pro.
 */
export function getMonthlyTotal(
  trips: import('@/types/trip').Trip[],
  country: string
): { totalKm: number; totalAmount: number; rate: number } {
  const proTrips = trips.filter(t => t.type === 'professionnel');

  let totalKm = 0;
  let totalAmount = 0;
  let lastRate = 0;

  proTrips.forEach(t => {
    const date = new Date(t.date);
    const { rate, amount } = calculateAllowance(t.distance, country, date);
    totalKm += t.distance;
    totalAmount += amount;
    lastRate = rate;
  });

  return {
    totalKm: parseFloat(totalKm.toFixed(1)),
    totalAmount: parseFloat(totalAmount.toFixed(2)),
    rate: lastRate,
  };
}

// ─── Compat legacy ────────────────────────────────────────────────────────────

/** Détecte automatiquement le pays selon les adresses */
export const detectCountry = (startAddress: string, endAddress: string): SupportedCountry => {
  const luxPattern = /luxembourg|esch[-\s]sur[-\s]alzette|differdange|dudelange|bettembourg|pétange|rumelange|schifflange|hesperange/i;
  const frPattern = /france|\bparis\b|\blyon\b|\bmarseille\b|\blille\b|\bstrasbourg\b/i;
  const dePattern = /deutschland|germany|\bberlin\b|\bhamburg\b|\bmünchen\b|\bfrankfurt\b/i;
  const nlPattern = /nederland|netherlands|\bamsterdam\b|\brotterdam\b|\butrecht\b/i;

  const combined = `${startAddress} ${endAddress}`;
  if (luxPattern.test(combined)) return 'LU';
  if (frPattern.test(combined)) return 'FR';
  if (dePattern.test(combined)) return 'DE';
  if (nlPattern.test(combined)) return 'NL';
  return 'BE';
};

/** Calcule l'indemnité pour un trajet (compat legacy) */
export const calculateIndemnity = (km: number, country: SupportedCountry): number => {
  const rate = getRate(country, new Date());
  return parseFloat((km * rate).toFixed(2));
};

/** Calcule les totaux d'indemnité sur une liste de trajets pro (compat legacy) */
export const calculateTotalIndemnity = (
  proTrips: {
    distance: number;
    country?: SupportedCountry;
    startAddress: string;
    endAddress: string;
    date?: string;
  }[]
): { totalBE: number; totalLU: number; grandTotal: number; kmBE: number; kmLU: number } => {
  let kmBE = 0;
  let kmLU = 0;
  let totalBE = 0;
  let totalLU = 0;

  proTrips.forEach(t => {
    const country = t.country ?? detectCountry(t.startAddress, t.endAddress);
    const date = t.date ? new Date(t.date) : new Date();
    const { amount } = calculateAllowance(t.distance, country, date);

    if (country === 'LU') {
      kmLU += t.distance;
      totalLU += amount;
    } else {
      kmBE += t.distance;
      totalBE += amount;
    }
  });

  return {
    kmBE: parseFloat(kmBE.toFixed(1)),
    kmLU: parseFloat(kmLU.toFixed(1)),
    totalBE: parseFloat(totalBE.toFixed(2)),
    totalLU: parseFloat(totalLU.toFixed(2)),
    grandTotal: parseFloat((totalBE + totalLU).toFixed(2)),
  };
};
